import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #isOpening = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:open-checkout-drawer', this.#openCheckoutDrawer);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:open-checkout-drawer', this.#openCheckoutDrawer);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    if (this.refs.dialog?.open || this.#isOpening) {
      this.#restorePageScrollLock();
    }
  }

  #handleCartAdd = (event) => {
    const eventData = event.detail?.data || {};
    const shouldOpenCheckoutDrawer = Number(eventData.itemCount) > 0;

    if (eventData.didError || (!this.hasAttribute('auto-open') && !shouldOpenCheckoutDrawer)) return;

    requestAnimationFrame(() => {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer instanceof CartDrawerComponent) {
        cartDrawer.#openCheckoutDrawer();
      }
    });
  };

  #openCheckoutDrawer = () => {
    if (this.refs.dialog?.open || this.#isOpening) return;

    this.#isOpening = true;
    this.showDialog();

    requestAnimationFrame(() => {
      this.#isOpening = false;
      this.querySelector('#checkout')?.focus();
    });
  };

  #restorePageScrollLock() {
    const top = document.body.style.top;
    const scrollY = top ? Math.abs(parseInt(top, 10)) || 0 : window.scrollY;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (top) {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
